import {
  type AnnouncementListResponse,
  type AnnouncementResponse,
  type CompanyLinkListResponse,
  type CompanyLinkResponse,
  type CreateAnnouncementRequest,
  type CreateCompanyLinkRequest,
  type UpdateAnnouncementRequest,
  type ReorderCompanyLinksRequest,
  type RenameCompanyLinkCategoryRequest,
  type UpdateCompanyLinkRequest,
  type ApproveCandidateRequest,
  type CandidateListResponse,
  type CandidateResponse,
  type DecideCandidateRequest,
  type StartSyncRequest,
  type SyncJobName,
  type SyncRunListResponse,
  type SyncRunResponse,
  type AuthConfigResponse,
  type ChangeCustomerStatusRequest,
  type ChangeProjectStatusRequest,
  type CreateCustomerRequest,
  type CreateExternalLinkRequest,
  type CreateProjectRelationRequest,
  type CreateProjectRequest,
  type CustomerListResponse,
  type CustomerResponse,
  type ExternalLinkListResponse,
  type ExternalLinkView,
  type MeResponse,
  type MockLoginRequest,
  type ProjectListResponse,
  type ProjectRelationListResponse,
  type ProjectResponse,
  type ProjectSuggestionsResponse,
  type SetProjectCustomersRequest,
  type SetProjectParticipantsRequest,
  type UpdateCustomerRequest,
  type UpdateExternalLinkRequest,
  type UpdateProjectRequest,
  announcementListResponseSchema,
  announcementReadsResponseSchema,
  announcementResponseSchema,
  type AnnouncementReadsResponse,
  companyLinkListResponseSchema,
  companyLinkResponseSchema,
  refreshFaviconsResponseSchema,
  type RefreshFaviconsResponse,
  apiErrorSchema,
  candidateListResponseSchema,
  candidateResponseSchema,
  syncRunListResponseSchema,
  syncRunResponseSchema,
  authConfigResponseSchema,
  customerListResponseSchema,
  customerResponseSchema,
  externalLinkListResponseSchema,
  externalLinkResponseSchema,
  meResponseSchema,
  portalUserListResponseSchema,
  portalUserResponseSchema,
  type PortalUserListResponse,
  type PortalUserResponse,
  type CreatePortalUserRequest,
  type UpdatePortalUserRequest,
  type SetPortalUserArchivedRequest,
  projectListResponseSchema,
  projectRelationListResponseSchema,
  projectResponseSchema,
  projectSuggestionsResponseSchema,
} from "@portal/schemas";
import {
  calendarListResponseSchema,
  type CalendarListResponse,
  type UpdateCalendarSelectionRequest,
  memoListResponseSchema,
  memoResponseSchema,
  memoUnreadPinsResponseSchema,
  projectMemoListResponseSchema,
  type MemoUnreadPinsResponse,
  type ProjectMemoListResponse,
  type ProjectMemoScope,
  type UpdateMemoPinnedRequest,
  type CreateMemoRequest,
  type MemoListResponse,
  type MemoResponse,
  type MemoScope,
  type UpdateMemoArchivedRequest,
  type UpdateMemoRequest,
  type UpdateMemoSharingRequest,
  calendarWeekResponseSchema,
  type CalendarWeekResponse,
  todoBoardResponseSchema,
  type TodoBoardResponse,
  todoSourceProjectsResponseSchema,
  type TodoSourceProjectsResponse,
  type CreateTodoCardRequest,
  type UpdateTodoCardRequest,
  type MoveTodoCardRequest,
  portalSettingsResponseSchema,
  type PortalSettingsResponse,
  type UpdateSystemAdminsRequest,
} from "@portal/schemas";
import { z, type ZodType } from "zod";

/** API のエラー応答。status と ApiError.code で分岐する。 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, schema: ZodType<T>, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
    credentials: "same-origin",
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const parsed = apiErrorSchema.safeParse(json);
    throw new ApiError(
      res.status,
      parsed.success ? parsed.data.code : `HTTP_${res.status}`,
      parsed.success ? parsed.data.message : "サーバーとの通信に失敗しました。",
    );
  }
  return schema.parse(json);
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

// ---- auth / me
export const fetchMe = (): Promise<MeResponse> => request("/api/me", meResponseSchema);
// ---- ユーザーマスタ(名前だけ。SPEC §2)
export const fetchUsers = (): Promise<PortalUserListResponse> =>
  request("/api/users", portalUserListResponseSchema);
export const createPortalUser = (body: CreatePortalUserRequest): Promise<PortalUserResponse> =>
  request("/api/admin/users", portalUserResponseSchema, json("POST", body));
/** ユーザーをアーカイブする・戻す(全体の管理者)。 */
export const setPortalUserArchived = (
  email: string,
  body: SetPortalUserArchivedRequest,
): Promise<PortalUserResponse> =>
  request(
    `/api/admin/users/${encodeURIComponent(email)}/archived`,
    portalUserResponseSchema,
    json("PATCH", body),
  );
/** 自分の名前を設定・変更する(本人も変えられる。2026-10-15)。 */
export const updateMyName = (body: UpdatePortalUserRequest): Promise<MeResponse> =>
  request("/api/me/name", meResponseSchema, json("PATCH", body));
export const updatePortalUser = (
  email: string,
  body: UpdatePortalUserRequest,
): Promise<PortalUserResponse> =>
  request(
    `/api/admin/users/${encodeURIComponent(email)}`,
    portalUserResponseSchema,
    json("PATCH", body),
  );
export const fetchAuthConfig = (): Promise<AuthConfigResponse> =>
  request("/api/auth/config", authConfigResponseSchema);
export const mockLogin = (body: MockLoginRequest): Promise<MeResponse> =>
  request("/api/auth/mock/login", meResponseSchema, json("POST", body));
export const logout = (): Promise<void> =>
  fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" }).then(() => undefined);

// ---- 全社向けカード(ブックマーク・お知らせ)

export const fetchCompanyLinks = (all = false): Promise<CompanyLinkListResponse> =>
  request(all ? "/api/company/links/all" : "/api/company/links", companyLinkListResponseSchema);
export const createCompanyLink = (body: CreateCompanyLinkRequest): Promise<CompanyLinkResponse> =>
  request("/api/company/links", companyLinkResponseSchema, json("POST", body));
export const updateCompanyLink = (
  id: string,
  body: UpdateCompanyLinkRequest,
): Promise<CompanyLinkResponse> =>
  request(
    `/api/company/links/${encodeURIComponent(id)}`,
    companyLinkResponseSchema,
    json("PATCH", body),
  );
export const reorderCompanyLinks = (
  body: ReorderCompanyLinksRequest,
): Promise<CompanyLinkListResponse> =>
  request("/api/company/links/order", companyLinkListResponseSchema, json("PUT", body));
/** すべてのリンクの favicon をサーバーで取り直す(管理者)。 */
export const refreshCompanyLinkFavicons = (): Promise<RefreshFaviconsResponse> =>
  request("/api/company/links-favicons/refresh", refreshFaviconsResponseSchema, json("POST", {}));
export const renameCompanyLinkCategory = (
  body: RenameCompanyLinkCategoryRequest,
): Promise<CompanyLinkListResponse> =>
  request("/api/company/link-categories/rename", companyLinkListResponseSchema, json("POST", body));
export const fetchAnnouncements = (all = false): Promise<AnnouncementListResponse> =>
  request(
    all ? "/api/company/announcements/all" : "/api/company/announcements",
    announcementListResponseSchema,
  );
/** 本人が既読にした、いま掲載中のお知らせの ID(ベルの未読の件数)。 */
export const fetchAnnouncementReads = (): Promise<AnnouncementReadsResponse> =>
  request("/api/company/announcements/reads", announcementReadsResponseSchema);
/** お知らせを既読にする(足すだけ)。 */
export const markAnnouncementsRead = (ids: string[]): Promise<AnnouncementReadsResponse> =>
  request(
    "/api/company/announcements/reads",
    announcementReadsResponseSchema,
    json("PUT", { ids }),
  );
export const createAnnouncement = (
  body: CreateAnnouncementRequest,
): Promise<AnnouncementResponse> =>
  request("/api/company/announcements", announcementResponseSchema, json("POST", body));
export const updateAnnouncement = (
  id: string,
  body: UpdateAnnouncementRequest,
): Promise<AnnouncementResponse> =>
  request(
    `/api/company/announcements/${encodeURIComponent(id)}`,
    announcementResponseSchema,
    json("PATCH", body),
  );

// ---- users

// ---- projects
export interface ProjectListParams {
  status?: "ACTIVE" | "ENDED" | "ARCHIVED";
  mine?: boolean;
}
export const fetchProjects = (params: ProjectListParams = {}): Promise<ProjectListResponse> => {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  if (params.mine) q.set("mine", "true");
  const qs = q.toString();
  return request(`/api/projects${qs ? `?${qs}` : ""}`, projectListResponseSchema);
};
export const fetchProject = (id: string): Promise<ProjectResponse> =>
  request(`/api/projects/${encodeURIComponent(id)}`, projectResponseSchema);
export const createProject = (body: CreateProjectRequest): Promise<ProjectResponse> =>
  request("/api/projects", projectResponseSchema, json("POST", body));
export const updateProject = (id: string, body: UpdateProjectRequest): Promise<ProjectResponse> =>
  request(`/api/projects/${encodeURIComponent(id)}`, projectResponseSchema, json("PATCH", body));
export const changeProjectStatus = (
  id: string,
  body: ChangeProjectStatusRequest,
): Promise<ProjectResponse> =>
  request(
    `/api/projects/${encodeURIComponent(id)}/status`,
    projectResponseSchema,
    json("PUT", body),
  );
export const setProjectCustomers = (
  id: string,
  body: SetProjectCustomersRequest,
): Promise<ProjectResponse> =>
  request(
    `/api/projects/${encodeURIComponent(id)}/customers`,
    projectResponseSchema,
    json("PUT", body),
  );
export const setProjectParticipants = (
  id: string,
  body: SetProjectParticipantsRequest,
): Promise<ProjectResponse> =>
  request(
    `/api/projects/${encodeURIComponent(id)}/participants`,
    projectResponseSchema,
    json("PUT", body),
  );

// ---- project relations / external links
export const fetchProjectRelations = (id: string): Promise<ProjectRelationListResponse> =>
  request(`/api/projects/${encodeURIComponent(id)}/relations`, projectRelationListResponseSchema);
export const addProjectRelation = (
  id: string,
  body: CreateProjectRelationRequest,
): Promise<ProjectRelationListResponse> =>
  request(
    `/api/projects/${encodeURIComponent(id)}/relations`,
    projectRelationListResponseSchema,
    json("POST", body),
  );
export const removeProjectRelation = (id: string, relationId: string): Promise<void> =>
  request(
    `/api/projects/${encodeURIComponent(id)}/relations/${encodeURIComponent(relationId)}`,
    z.void(),
    { method: "DELETE" },
  );
export const fetchProjectLinks = (id: string): Promise<ExternalLinkListResponse> =>
  request(`/api/projects/${encodeURIComponent(id)}/links`, externalLinkListResponseSchema);
export const addProjectLink = (
  id: string,
  body: CreateExternalLinkRequest,
): Promise<{ link: ExternalLinkView }> =>
  request(
    `/api/projects/${encodeURIComponent(id)}/links`,
    externalLinkResponseSchema,
    json("POST", body),
  );
export const updateProjectLink = (
  id: string,
  linkId: string,
  body: UpdateExternalLinkRequest,
): Promise<{ link: ExternalLinkView }> =>
  request(
    `/api/projects/${encodeURIComponent(id)}/links/${encodeURIComponent(linkId)}`,
    externalLinkResponseSchema,
    json("PATCH", body),
  );
export const removeProjectLink = (id: string, linkId: string): Promise<void> =>
  request(`/api/projects/${encodeURIComponent(id)}/links/${encodeURIComponent(linkId)}`, z.void(), {
    method: "DELETE",
  });

export const fetchProjectSuggestions = (
  id: string,
  refresh = false,
): Promise<ProjectSuggestionsResponse> =>
  request(
    `/api/projects/${encodeURIComponent(id)}/suggestions${refresh ? "?refresh=true" : ""}`,
    projectSuggestionsResponseSchema,
  );

// ---- customers
export const fetchCustomers = (status?: "ACTIVE" | "ARCHIVED"): Promise<CustomerListResponse> =>
  request(`/api/customers${status ? `?status=${status}` : ""}`, customerListResponseSchema);
export const fetchCustomerPermissions = (): Promise<{ canManage: boolean }> =>
  request("/api/customers/permissions", z.object({ canManage: z.boolean() }));
export const createCustomer = (body: CreateCustomerRequest): Promise<CustomerResponse> =>
  request("/api/customers", customerResponseSchema, json("POST", body));
export const updateCustomer = (
  id: string,
  body: UpdateCustomerRequest,
): Promise<CustomerResponse> =>
  request(`/api/customers/${encodeURIComponent(id)}`, customerResponseSchema, json("PATCH", body));
export const changeCustomerStatus = (
  id: string,
  body: ChangeCustomerStatusRequest,
): Promise<CustomerResponse> =>
  request(
    `/api/customers/${encodeURIComponent(id)}/status`,
    customerResponseSchema,
    json("PUT", body),
  );

// ---- candidates
export interface CandidateListParams {
  status?: "PENDING" | "APPROVED" | "REJECTED" | "ON_HOLD";
}
export const fetchCandidates = (params: CandidateListParams = {}): Promise<CandidateListResponse> =>
  request(
    `/api/candidates${params.status ? `?status=${params.status}` : ""}`,
    candidateListResponseSchema,
  );
export const approveCandidate = (
  id: string,
  body: ApproveCandidateRequest,
): Promise<CandidateResponse> =>
  request(
    `/api/candidates/${encodeURIComponent(id)}/approve`,
    candidateResponseSchema,
    json("POST", body),
  );
export const decideCandidate = (
  id: string,
  action: "reject" | "hold",
  body: DecideCandidateRequest,
): Promise<CandidateResponse> =>
  request(
    `/api/candidates/${encodeURIComponent(id)}/${action}`,
    candidateResponseSchema,
    json("POST", body),
  );

// ---- admin sync
export const fetchSyncRuns = (): Promise<SyncRunListResponse> =>
  request("/api/admin/sync/runs", syncRunListResponseSchema);
export const runSync = (job: SyncJobName, body: StartSyncRequest): Promise<SyncRunResponse> =>
  request(`/api/admin/sync/${job}/run`, syncRunResponseSchema, json("POST", body));

// ---- ポータル設定(全体の管理者)
export const fetchPortalSettings = (): Promise<PortalSettingsResponse> =>
  request("/api/admin/settings", portalSettingsResponseSchema);
export const updateSystemAdmins = (
  body: UpdateSystemAdminsRequest,
): Promise<PortalSettingsResponse> =>
  request("/api/admin/settings/system-admins", portalSettingsResponseSchema, json("PUT", body));

// ---- Google カレンダー(本人の予定)
/** date(YYYY-MM-DD)を含む週(月曜〜日曜、日本時間)の予定。付けなければ今週。 */
export const fetchCalendarWeek = (date?: string): Promise<CalendarWeekResponse> =>
  request(
    `/api/calendar/week${date ? `?date=${encodeURIComponent(date)}` : ""}`,
    calendarWeekResponseSchema,
  );

/** 選べるカレンダー(自分のもの・共有されたもの)と、いま表示しているもの。 */
export const fetchCalendarList = (): Promise<CalendarListResponse> =>
  request("/api/calendar/calendars", calendarListResponseSchema);
/** 表示するカレンダーを選ぶ。 */
export const updateCalendarSelection = (
  body: UpdateCalendarSelectionRequest,
): Promise<CalendarListResponse> =>
  request("/api/calendar/selection", calendarListResponseSchema, json("PUT", body));

// ---- メモ(個人メモ。見えるのは所有者と共有された人だけ)
export interface MemoListParams {
  q?: string;
  scope?: MemoScope;
}
export const fetchMemos = (params: MemoListParams = {}): Promise<MemoListResponse> => {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.scope && params.scope !== "all") qs.set("scope", params.scope);
  const suffix = qs.size > 0 ? `?${qs}` : "";
  return request(`/api/memos${suffix}`, memoListResponseSchema);
};
export const fetchMemo = (id: string): Promise<MemoResponse> =>
  request(`/api/memos/${encodeURIComponent(id)}`, memoResponseSchema);
export const createMemo = (body: CreateMemoRequest): Promise<MemoResponse> =>
  request("/api/memos", memoResponseSchema, json("POST", body));
export const updateMemo = (id: string, body: UpdateMemoRequest): Promise<MemoResponse> =>
  request(`/api/memos/${encodeURIComponent(id)}`, memoResponseSchema, json("PATCH", body));
export const updateMemoSharing = (
  id: string,
  body: UpdateMemoSharingRequest,
): Promise<MemoResponse> =>
  request(`/api/memos/${encodeURIComponent(id)}/sharing`, memoResponseSchema, json("PUT", body));
export const updateMemoArchived = (
  id: string,
  body: UpdateMemoArchivedRequest,
): Promise<MemoResponse> =>
  request(`/api/memos/${encodeURIComponent(id)}/archived`, memoResponseSchema, json("PUT", body));
export const deleteMemo = (id: string): Promise<void> =>
  request(`/api/memos/${encodeURIComponent(id)}`, z.void(), { method: "DELETE" });

// ---- プロジェクトのメモと「まずはここを見よう」ボード(SPEC §13.6)
export interface ProjectMemoListParams {
  q?: string;
  scope?: ProjectMemoScope;
}
export const fetchProjectMemos = (
  projectId: string,
  params: ProjectMemoListParams = {},
): Promise<ProjectMemoListResponse> => {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.scope && params.scope !== "all") qs.set("scope", params.scope);
  const suffix = qs.size > 0 ? `?${qs}` : "";
  return request(
    `/api/projects/${encodeURIComponent(projectId)}/memos${suffix}`,
    projectMemoListResponseSchema,
  );
};
/** ボードにピンで留める・外す(参加者だけ)。 */
export const updateMemoPinned = (
  id: string,
  body: UpdateMemoPinnedRequest,
): Promise<MemoResponse> =>
  request(`/api/memos/${encodeURIComponent(id)}/pinned`, memoResponseSchema, json("PUT", body));
/** メモを開いたことを記録する(ボードの未読の印を消す)。 */
export const markMemoRead = (id: string): Promise<void> =>
  request(`/api/memos/${encodeURIComponent(id)}/read`, z.void(), { method: "POST" });
/** ボードのピンのうち、自分にとって未読・更新のものの件数(プロジェクトごと)。 */
export const fetchMemoUnreadPins = (): Promise<MemoUnreadPinsResponse> =>
  request("/api/memos/unread-pins", memoUnreadPinsResponseSchema);

/** 画面表示用のエラー文言。 */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return "通信に失敗しました。";
  return "エラーが発生しました。";
}

// ---- ToDo ボード(SPEC §10)
export const fetchTodoBoard = (): Promise<TodoBoardResponse> =>
  request("/api/todos", todoBoardResponseSchema);
/** 取り込み元(Backlog 等)から、いますぐ取り込み直す。 */
export const syncTodoBoard = (): Promise<TodoBoardResponse> =>
  request("/api/todos/sync", todoBoardResponseSchema, json("POST", {}));
export const createTodoCard = (body: CreateTodoCardRequest): Promise<TodoBoardResponse> =>
  request("/api/todos/cards", todoBoardResponseSchema, json("POST", body));
export const updateTodoCard = (
  id: string,
  body: UpdateTodoCardRequest,
): Promise<TodoBoardResponse> =>
  request(
    `/api/todos/cards/${encodeURIComponent(id)}`,
    todoBoardResponseSchema,
    json("PATCH", body),
  );
export const moveTodoCard = (id: string, body: MoveTodoCardRequest): Promise<TodoBoardResponse> =>
  request(
    `/api/todos/cards/${encodeURIComponent(id)}/position`,
    todoBoardResponseSchema,
    json("PUT", body),
  );
export const deleteTodoCard = (id: string): Promise<TodoBoardResponse> =>
  request(`/api/todos/cards/${encodeURIComponent(id)}`, todoBoardResponseSchema, {
    method: "DELETE",
  });
/** Backlog の取り込みから除外できるプロジェクト(本人が参加しているもの)と、いま除外しているか。 */
export const fetchBacklogProjects = (): Promise<TodoSourceProjectsResponse> =>
  request("/api/todos/sources/backlog/projects", todoSourceProjectsResponseSchema);
/** Backlog の取り込みから除外するプロジェクト(置き換え)。そのプロジェクトのカードは消える。 */
export const updateBacklogExcludedProjects = (projectKeys: string[]): Promise<TodoBoardResponse> =>
  request(
    "/api/todos/sources/backlog/excluded-projects",
    todoBoardResponseSchema,
    json("PUT", { projectKeys }),
  );
/** Backlog 連携の解除(保存したトークンを消す)。 */
export const disconnectBacklog = (): Promise<void> =>
  request("/api/backlog/connection", z.void(), { method: "DELETE" });
